import type { CSSProperties } from 'react';
import { SubpageShell } from '../../components/layout/SubpageShell';
import { CtaBand, HeroBlobs } from '../../components/subpage/blocks';
import { productMeta } from '../../content/site';
import { urunlerPage as copy, type PlanTone } from '../../content/pages/urunler';

/** Paket etiketinin rengi: tüm paketler mavi, AI yeşil, üst paket ve beta revize tasarımındaki sarı. */
const PLAN_TONE: Record<PlanTone, string> = {
  all: 'border-blue-400/30 bg-blue-500/[0.12] text-[#93c5fd]',
  ai: 'border-[rgba(92,214,190,0.4)] bg-[rgba(92,214,190,0.12)] text-[#5cd6be]',
  top: 'border-[rgba(232,194,106,0.4)] bg-[rgba(232,194,106,0.1)] text-[#e8c26a]',
  beta: 'border-[rgba(232,194,106,0.4)] bg-[rgba(232,194,106,0.1)] text-[#e8c26a]',
};

/**
 * Tüm Ürünler (/ozellikler): menüdeki "Ürünler" başlığının ve ana sayfadaki "Tüm Ürünler" düğmesinin adresi.
 * Her modül bir kart: ad + paket etiketi, vaat, kısa açıklama; kart ürün sayfasına gider. Altta henüz yayında
 * olmayan çözümler kesik çizgili kutuda.
 */
export default function TumUrunlerPage() {
  const { hero } = copy;

  return (
    <SubpageShell pageKey={copy.href}>
      <section className="relative overflow-hidden pb-6 pt-12 lg:pt-[72px]">
        <HeroBlobs accent="#45c0ee" opacity={0.16} />
        <div className="relative mx-auto flex max-w-[1200px] flex-col gap-5 px-4 sm:px-6">
          <span className="zh-crumb self-start">{hero.crumb}</span>
          <h1 className="m-0 max-w-[820px] font-display text-[36px] font-semibold leading-[1.06] tracking-[-0.035em] text-white sm:text-[44px] xl:text-[52px]">
            {hero.title} <span className="zh-grad-text">{hero.titleAccent}</span>
          </h1>
          <p className="m-0 max-w-[720px] text-[17px] leading-[1.65] text-ink-soft">{hero.lead}</p>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-4 pt-10 sm:px-6">
        <div className="grid gap-[18px] sm:grid-cols-2 lg:grid-cols-3">
          {copy.items.map((item, i) => {
            const p = productMeta[item.id];
            return (
              <a
                key={item.id}
                href={p.href}
                className="zh-pg-card zh-reveal flex flex-col gap-3 text-inherit no-underline"
                style={{ transitionDelay: `${(i % 3) * 0.08}s` } as CSSProperties}
              >
                <span className="flex items-start justify-between gap-3">
                  <span
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] border"
                    style={{
                      color: p.color,
                      background: `rgba(${p.rgb},0.12)`,
                      borderColor: `rgba(${p.rgb},0.3)`,
                      boxShadow: `0 0 24px rgba(${p.rgb},0.18)`,
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d={p.icon} />
                    </svg>
                  </span>
                  <span className={`rounded-full border px-2.5 py-1 text-[11.5px] font-bold ${PLAN_TONE[item.plan.tone]}`}>{item.plan.label}</span>
                </span>
                <span className="text-[12px] font-bold uppercase tracking-[0.14em]" style={{ color: p.color }}>
                  {item.name ?? p.title}
                </span>
                <h2 className="m-0 font-display text-[20px] font-semibold leading-[1.25] tracking-[-0.015em] text-white">{item.title}</h2>
                <p className="m-0 text-[14.5px] leading-[1.6] text-ink-soft">{item.desc}</p>
                <span className="mt-auto inline-flex min-h-10 items-center text-[14px] font-bold text-action-text">{copy.inspect}</span>
              </a>
            );
          })}
        </div>

        <div className="zh-reveal mt-[18px] flex flex-col gap-4 rounded-[20px] border border-dashed border-[rgba(120,150,210,0.32)] px-6 py-5 sm:flex-row sm:items-center sm:gap-8">
          <span className="text-[12px] font-bold uppercase tracking-[0.18em] text-ink-subdued">{copy.soon.label}</span>
          {copy.soon.items.map((s) => (
            <p key={s.title} className="m-0 text-[14.5px] leading-[1.55] text-ink-soft">
              <b className="text-white">{s.title}:</b> {s.desc}
            </p>
          ))}
        </div>
      </section>

      <CtaBand {...copy.cta} />
    </SubpageShell>
  );
}
